import React from 'react';
import PropTypes from 'prop-types';
import arrayMutators from 'final-form-arrays';
import { Col, Row } from 'reactstrap';
import { H3, H5, P } from 'components/Typography';
import { TextareaInput, TextInput } from 'components/Form';
import Button from 'components/Button';
import ModuleList from 'routes/EngageAdmin/LearningPath/components/ModuleList';
import LearningPathSidebar from 'routes/EngageAdmin/LearningPath/components/LearningPathSidebar';
import { Form } from 'react-final-form';
import styled from 'styled-components/macro';
import { theme } from 'styled-tools';
import { useHistory } from 'react-router-dom';
import DeleteTemplateAction from 'routes/EngageAdmin/LearningPath/components/DeleteTemplateAction';
import { INF_LEARNING_TEMPLATE, LEARNING_PATH } from 'utils/consts';
import { getToken } from 'utils/auth';
import FieldLabel from 'components/Form/FieldLabel';
import { formatModuleRunningTime } from 'utils/functions';

const ContentSection = styled.div`
  background: ${theme('colors.white')};
  box-shadow: ${theme('boxShadow')};
  border-radius: ${theme('borderRadius')};
  margin-bottom: 1rem;
  padding: 1rem;
`;

const ActionsWrapper = styled.div`
  ${Button} {
    width: 100%;
  }

  ${Button} + ${Button} {
    margin-top: 1rem;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  > ${Button} + ${Button} {
    margin-left: 1rem;
  }

  ${Button} {
    width: calc(50% - 0.5rem);
    margin-top: 1rem;
  }
`;

const SaveToPdfButton = styled(Button)`
  margin-top: 1rem;
`;

const validate = values => {
  const errors = {};

  if (!values.name) {
    errors.name = 'You must enter a name';
  }

  if (!values.description) {
    errors.description = 'You must enter a description';
  }

  if (!values.modules || values.modules.length === 0) {
    errors.modules = 'You must have at least one module';
  }

  return errors;
};

function LearningPathForm({ onSubmit, initialValues = {}, currentView = LEARNING_PATH }) {
  const { push: redirectTo } = useHistory();
  const { id: learningPathId, totalRunTime } = initialValues;

  const handleSaveToPDF = e => {
    e.preventDefault();

    fetch(`${process.env.REACT_APP_API_URL}/api/download-learning-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getToken(),
      },
      body: JSON.stringify({
        learningPathId: learningPathId,
        currentView: currentView,
      }),
    })
      .then(res => res.blob())
      .then(blob => {
        window.open(window.URL.createObjectURL(blob), '_blank');
      });
  };

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
      }}
      render={({ handleSubmit, submitting }) => {
        return (
          <form onSubmit={handleSubmit} disabled={submitting}>
            <Row>
              <Col md='8'>
                <ContentSection>
                  {currentView === INF_LEARNING_TEMPLATE && (
                    <>
                      <H3>Infinitec Learning Template Details</H3>
                      <P>
                        Infinitec Learning Templates are named groups of presentations from the Online Classroom that
                        are created by Infinitec for the most commonly used presentations. When a copy is saved, a
                        Learning Templates is created and may be used to create Learning Campaigns.
                      </P>
                    </>
                  )}
                  {currentView !== INF_LEARNING_TEMPLATE && (
                    <>
                      <H3>Learning Template Details</H3>
                      <P>
                        Learning Templates are named groups of presentations from the Online Classroom, created by you
                        or other Training Administrators from your agency. Once created, Learning Templates are saved
                        and may be used to create Learning Campaigns.
                      </P>
                    </>
                  )}
                  <TextInput label='Name' name='name' placeholder='Name' heightAuto />
                  <TextareaInput label='Description' name='description' placeholder='Description' />

                  <FieldLabel>Total Seat Time: {totalRunTime ? formatModuleRunningTime(totalRunTime) : '0'}</FieldLabel>

                  <ModuleList />
                </ContentSection>
              </Col>
              <Col md='4'>
                <ContentSection>
                  <ActionsWrapper>
                    <H5>Template Actions</H5>

                    <ButtonWrapper flush>
                      <Button type='submit'>Save{currentView === INF_LEARNING_TEMPLATE && ' as Copy'}</Button>
                      <Button kind='tertiary' onClick={() => redirectTo('/engage-admin')}>
                        Cancel
                      </Button>
                    </ButtonWrapper>
                    {initialValues?.id && (
                      <>
                        <SaveToPdfButton onClick={e => handleSaveToPDF(e)} fullWidth={true}>
                          Save PDF Catalog
                        </SaveToPdfButton>
                        {currentView !== INF_LEARNING_TEMPLATE && (
                          <DeleteTemplateAction learningPathId={initialValues?.id} />
                        )}
                      </>
                    )}
                  </ActionsWrapper>
                </ContentSection>
                <ContentSection>
                  <LearningPathSidebar />
                </ContentSection>
              </Col>
            </Row>
          </form>
        );
      }}
    />
  );
}

LearningPathForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  currentView: PropTypes.string,
};

export default LearningPathForm;
